/**
 * Domain types, mirroring the PocketBase schema and the Python domain package.
 *
 * Nothing in `src/domain` may import from React, the DOM, or Vite — a future
 * React Native app reuses this directory verbatim.
 */

export const KINDS = ['document', 'subscription', 'contract', 'other'] as const
export type Kind = (typeof KINDS)[number]

export const STATUSES = ['active', 'archived', 'cancelled'] as const
export type Status = (typeof STATUSES)[number]

export const BILLING_CYCLES = ['one_off', 'monthly', 'quarterly', 'annual'] as const
export type BillingCycle = (typeof BILLING_CYCLES)[number]

export const RENEWAL_PERIODS = ['none', 'monthly', 'quarterly', 'annual'] as const
export type RenewalPeriod = (typeof RENEWAL_PERIODS)[number]

export const CURRENCIES = ['GBP', 'USD', 'EUR'] as const
export type Currency = (typeof CURRENCIES)[number]

/** A record as PocketBase returns it. Dates arrive as "2026-06-01 00:00:00.000Z". */
export interface ItemRecord {
  id: string
  owner: string
  kind: Kind
  name: string
  provider: string
  /** Date-only in meaning, but PocketBase stores and returns a datetime string. */
  expiry_date: string
  notice_period_days: number
  auto_renews: boolean
  renewal_period: RenewalPeriod
  /** Integer minor units (pence/cents) — never a float. */
  cost_minor: number
  currency: Currency
  billing_cycle: BillingCycle
  /** null or [] ⇒ inherit the user's default offsets. */
  reminder_offsets: number[] | null
  note: string
  reference_last4: string
  status: Status
  created: string
  updated: string
}

/** The fields a user can write. `owner` is set by the client from the session. */
export type ItemInput = Omit<ItemRecord, 'id' | 'owner' | 'created' | 'updated'>

export interface UserRecord {
  id: string
  email: string
  verified: boolean
  name: string
  timezone: string
  digest_hour: number
  default_offsets: number[] | null
  email_reminders: boolean
}

export type Urgency = 'overdue' | 'today' | 'soon' | 'later'

/** An item with the derived fields the UI actually renders. */
export interface DerivedItem {
  item: ItemRecord
  expiryDate: Date
  /** expiry_date - notice_period_days: the date that actually matters. */
  actionBy: Date
  daysRemaining: number
  urgency: Urgency
  /** Cost normalised to a month, in minor units. 0 when there is no cost. */
  monthlyMinor: number
  annualMinor: number
}

/** UI labels only — stored values stay document / subscription / contract / other. */
export const KIND_LABELS: Record<Kind, string> = {
  document: 'Documents & IDs',
  subscription: 'Subscriptions',
  contract: 'Contracts & plans',
  other: 'Other',
}

export const BILLING_CYCLE_LABELS: Record<BillingCycle, string> = {
  one_off: 'One-off',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
}

export const RENEWAL_PERIOD_LABELS: Record<RenewalPeriod, string> = {
  none: 'Does not renew',
  monthly: 'Monthly',
  quarterly: 'Quarterly',
  annual: 'Annual',
}

export const CURRENCY_SYMBOLS: Record<Currency, string> = {
  GBP: '£',
  USD: '$',
  EUR: '€',
}

export const DEFAULT_OFFSETS = [90, 30, 7, 1] as const
